import { useRef, useMemo, useEffect, Suspense } from 'react';
import { Canvas, useFrame, useThree, useLoader } from '@react-three/fiber';
import { Environment } from '@react-three/drei';
import * as THREE from 'three';

const ptr = { x: 0, y: 0, tx: 0, ty: 0 };

function Tracker() {
  const { size, viewport } = useThree();
  useEffect(() => {
    const h = (e) => {
      ptr.tx = (e.clientX / window.innerWidth) * 2 - 1;
      ptr.ty = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener('mousemove', h, { passive: true });
    return () => window.removeEventListener('mousemove', h);
  }, [size, viewport]);
  useFrame((_, delta) => {
    const l = 1 - Math.pow(0.001, delta);
    ptr.x += (ptr.tx - ptr.x) * l * 2.5;
    ptr.y += (ptr.ty - ptr.y) * l * 2.5;
  });
  return null;
}

function Bar({ follow }) {
  const ref = useRef();
  const { viewport } = useThree();
  useFrame(({ clock }, delta) => {
    const t = clock.elapsedTime;
    const m = ref.current;
    const mob = viewport.width < 6;
    if (follow) {
      m.position.x += (ptr.x * (mob ? 1 : 2.5) - m.position.x) * delta * 3;
      m.position.y += (ptr.y * (mob ? 0.6 : 1.6) + Math.sin(t * 0.8) * 0.2 - m.position.y) * delta * 3;
    } else {
      m.position.y = Math.sin(t * 0.6) * 0.25;
    }
    m.rotation.x += delta * 0.3;
    m.rotation.y += delta * 0.5;
    if (follow) m.rotation.z += (ptr.x * 0.2 - m.rotation.z) * delta * 4;
    const s = 1 + Math.sin(t * 1.1) * 0.015;
    m.scale.setScalar(s);
  });
  return (
    <mesh ref={ref}>
      <boxGeometry args={[2.4, 0.65, 1.15]} />
      <meshStandardMaterial color="#C9A84C" metalness={0.96} roughness={0.08} envMapIntensity={2.2} />
    </mesh>
  );
}

function Coin({ r = 3, spd = 0.4, ph = 0, sc = 0.8 }) {
  const ref = useRef();
  useFrame(({ clock }, delta) => {
    const t = clock.elapsedTime;
    const a = t * spd + ph;
    const m = ref.current;
    m.position.x += (Math.cos(a) * r + ptr.x * 1 - m.position.x) * delta * 3;
    m.position.y += (Math.sin(t * 1 + ph) * 0.35 + ptr.y * 0.5 - m.position.y) * delta * 3;
    m.position.z = Math.sin(a) * r * 0.35;
    m.rotation.x += delta * 0.8;
    m.rotation.y += delta * 1.1;
  });
  return (
    <mesh ref={ref} scale={sc}>
      <cylinderGeometry args={[0.6, 0.6, 0.1, 32]} />
      <meshStandardMaterial color="#D4B04A" metalness={0.98} roughness={0.05} envMapIntensity={2.5} />
    </mesh>
  );
}

function SmallBar({ pos, drift = 0.35, sc = 0.35 }) {
  const ref = useRef();
  const base = useMemo(() => new THREE.Vector3(...pos), [pos]);
  useFrame(({ clock }, delta) => {
    const t = clock.elapsedTime;
    const m = ref.current;
    const tx = base.x + Math.sin(t * drift) * 1.1 + ptr.x * 0.5;
    const ty = base.y + Math.cos(t * drift * 1.2) * 0.6 + ptr.y * 0.35;
    m.position.x += (tx - m.position.x) * delta * 2.5;
    m.position.y += (ty - m.position.y) * delta * 2.5;
    m.rotation.x += delta * 0.2;
    m.rotation.y += delta * 0.35;
  });
  return (
    <mesh ref={ref} scale={sc}>
      <boxGeometry args={[2.2, 0.65, 1]} />
      <meshStandardMaterial color="#A07D2E" metalness={0.9} roughness={0.15} envMapIntensity={1.5} />
    </mesh>
  );
}

function Dust() {
  const ref = useRef();
  const count = 90;
  const positions = useMemo(() => {
    const a = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      a[i * 3] = (Math.random() - 0.5) * 20;
      a[i * 3 + 1] = (Math.random() - 0.5) * 20;
      a[i * 3 + 2] = (Math.random() - 0.5) * 12;
    }
    return a;
  }, []);
  useFrame((_, delta) => {
    ref.current.rotation.y += delta * 0.02;
    ref.current.position.x += (ptr.x * 0.3 - ref.current.position.x) * delta * 2;
    ref.current.position.y += (ptr.y * 0.2 - ref.current.position.y) * delta * 2;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.025} color="#C9A84C" transparent opacity={0.35} sizeAttenuation />
    </points>
  );
}

function Book() {
  const ref = useRef();
  const coverTexture = useLoader(THREE.TextureLoader, '/book-cover.png');

  useFrame(({ clock }, delta) => {
    const t = clock.elapsedTime;
    const g = ref.current;
    const ty = -0.2 + ptr.x * 0.35 + Math.sin(t * 0.2) * 0.06;
    const tx = 0.03 + ptr.y * 0.12 + Math.sin(t * 0.15) * 0.03;
    g.rotation.y += (ty - g.rotation.y) * delta * 3;
    g.rotation.x += (tx - g.rotation.x) * delta * 3;
    g.rotation.z += (ptr.x * 0.03 - g.rotation.z) * delta * 3;
    g.position.y += (Math.sin(t * 0.5) * 0.12 - g.position.y) * delta * 3;
    g.position.x += (ptr.x * 0.2 - g.position.x) * delta * 3;
  });

  return (
    <group ref={ref}>
      {/* Book body */}
      <mesh><boxGeometry args={[2.8, 3.8, 0.26]} /><meshStandardMaterial color="#0F0F20" metalness={0.2} roughness={0.8} /></mesh>
      {/* Front cover — real book image */}
      <mesh position={[0, 0, 0.135]}>
        <planeGeometry args={[2.8, 3.8]} />
        <meshStandardMaterial map={coverTexture} roughness={0.4} metalness={0.05} />
      </mesh>
      {/* Spine */}
      <mesh position={[-1.4, 0, -0.005]}><boxGeometry args={[0.06, 3.8, 0.3]} /><meshStandardMaterial color="#C9A84C" metalness={0.9} roughness={0.12} /></mesh>
      {/* Pages */}
      <mesh position={[0, 0, -0.13]}><boxGeometry args={[2.7, 3.7, 0.012]} /><meshStandardMaterial color="#F0E8D0" roughness={0.9} /></mesh>
    </group>
  );
}

function HeroBook() {
  const ref = useRef();
  const { viewport } = useThree();
  const coverTexture = useLoader(THREE.TextureLoader, '/book-cover.png');
  const isMobile = viewport.width < 6;

  useFrame(({ clock }, delta) => {
    const t = clock.elapsedTime;
    const g = ref.current;
    const mx = isMobile ? 0.5 : 1.8;
    const my = isMobile ? 0.3 : 1.0;
    g.position.x += (ptr.x * mx - g.position.x) * delta * 2;
    g.position.y += (ptr.y * my + Math.sin(t * 0.6) * 0.2 - g.position.y) * delta * 2;
    g.position.z = 1.5 + Math.sin(t * 0.3) * 0.3;
    g.rotation.y += (-0.3 + ptr.x * 0.25 + Math.sin(t * 0.2) * 0.05 - g.rotation.y) * delta * 2.5;
    g.rotation.x += (0.05 + ptr.y * 0.1 - g.rotation.x) * delta * 2.5;
    const s = 0.5 + Math.sin(t * 0.8) * 0.01;
    g.scale.setScalar(s);
  });

  return (
    <group ref={ref} position={[0, 0, 1.5]}>
      <mesh><boxGeometry args={[2.8, 3.8, 0.26]} /><meshStandardMaterial color="#0F0F20" metalness={0.2} roughness={0.8} /></mesh>
      <mesh position={[0, 0, 0.135]}><planeGeometry args={[2.8, 3.8]} /><meshStandardMaterial map={coverTexture} roughness={0.4} metalness={0.05} /></mesh>
      <mesh position={[-1.4, 0, -0.005]}><boxGeometry args={[0.06, 3.8, 0.3]} /><meshStandardMaterial color="#C9A84C" metalness={0.9} roughness={0.12} /></mesh>
      <mesh position={[0, 0, -0.13]}><boxGeometry args={[2.7, 3.7, 0.012]} /><meshStandardMaterial color="#F0E8D0" roughness={0.9} /></mesh>
    </group>
  );
}

function Lights() {
  return (
    <>
      <ambientLight intensity={0.15} />
      <directionalLight position={[5, 5, 4]} intensity={1.2} color="#FFF5D4" />
      <directionalLight position={[-3, -2, -3]} intensity={0.3} color="#C9A84C" />
      <pointLight position={[0, 0, 4]} intensity={0.6} color="#C9A84C" distance={10} />
    </>
  );
}

export function GoldCanvas({ follow = true, cam = [0, 0, 6.5], style, className }) {
  return (
    <div className={className} style={{ width: '100%', height: '100%', ...style }}>
      <Canvas camera={{ position: cam, fov: 42 }} dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
        style={{ background: 'transparent' }} frameloop="always">
        <Suspense fallback={null}>
          <Tracker />
          <Lights />
          <Environment preset="city" />
          <Bar follow={follow} />
          <Coin r={3} spd={0.4} ph={0} sc={0.8} />
          <Coin r={2.4} spd={0.6} ph={Math.PI} sc={0.55} />
          <Coin r={3.5} spd={0.28} ph={Math.PI / 2} sc={0.4} />
          <SmallBar pos={[-3, -1.3, -2]} drift={0.28} sc={0.32} />
          <SmallBar pos={[3.2, 1.3, -2.5]} drift={0.38} sc={0.28} />
          <HeroBook />
          <Dust />
        </Suspense>
      </Canvas>
    </div>
  );
}

export function BookCanvas({ style, className }) {
  return (
    <div className={className} style={{ width: '100%', height: '100%', ...style }}>
      <Canvas camera={{ position: [0, 0, 6], fov: 38 }} dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true }} style={{ background: 'transparent' }} frameloop="always">
        <Suspense fallback={null}>
          <Tracker />
          <Lights />
          <Environment preset="city" />
          <Book />
          <Dust />
        </Suspense>
      </Canvas>
    </div>
  );
}
